/**
 * Sentry environment resolution for deployment-aware tagging.
 * Cloud Functions always set NODE_ENV=production, so explicit SENTRY_ENVIRONMENT is required
 * to distinguish development/staging from production deployments.
 */

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

/** True when the browser hostname is a loopback address (local dev). */
export function isLocalhostHostname(hostname: string | undefined | null): boolean {
  if (!hostname) return false;
  return LOCAL_HOSTNAMES.has(hostname.trim().toLowerCase());
}

/**
 * Whether the web app should initialize Sentry in the browser.
 * Disabled on localhost by default to avoid noisy dev traffic and 429 rate limits.
 * Set `forceLocal: true` (or VITE_SENTRY_ENABLE_LOCAL=true) to opt in locally.
 */
export function shouldEnableBrowserSentry(options: {
  dsn: string | undefined | null;
  hostname?: string | undefined | null;
  forceLocal?: boolean | string | undefined | null;
}): boolean {
  if (!options.dsn?.trim()) return false;

  const forceLocal = options.forceLocal === true || options.forceLocal === 'true';
  if (forceLocal) return true;

  const hostname =
    options.hostname ??
    (typeof globalThis !== 'undefined' &&
    'location' in globalThis &&
    globalThis.location &&
    typeof globalThis.location.hostname === 'string'
      ? globalThis.location.hostname
      : null);

  return !isLocalhostHostname(hostname);
}

export function resolveSentryEnvironment(
  explicitEnvironment: string | undefined | null,
  fallback: string | undefined | null = 'development'
): string {
  const explicit = explicitEnvironment?.trim();
  if (explicit) return explicit;

  const fb = fallback?.trim();
  if (fb) return fb;

  return 'development';
}

/** Trace/replay sampling: full rate outside production Sentry environment. */
export function resolveSentryTracesSampleRate(
  environment: string,
  productionRate = 0.1,
  nonProductionRate = 1.0
): number {
  return environment === 'production' ? productionRate : nonProductionRate;
}
